import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/styles";
import { 
    Grid, 
    Typography, 
    TextField, 
    FormControlLabel, 
    Checkbox,
    Divider,
    Button,
    Paper,
    Card,
    CardHeader,
    CardContent,
    CircularProgress
} from "@material-ui/core";
import { ValidatorForm } from "react-material-ui-form-validator";
import {
    Page,
    PageContent,
    FormActions,
    FormButton,
    FormSubmitButton,
    ScrollToTopOnMount,
    SearchDropdown,
    RadioListInput
} from "frame/components";
import { PageHeaderTabs } from "features/health-safety-forms/components";
import { useRouter } from "utils";
import { onSiteInspectionChecklistCreateRequest, onSiteInspectionChecklistListRequest, onSiteInspectionChecklistFormReset } from "features/health-and-safety/actions";
import { onList as onStaffListRequest } from "features/staff/actions";
import uuid from "uuid";
import { getMockSiteInspectionChecklists } from "./models/mock-data";
import { SiteForm } from "features/forms";
import * as fromHealthSafetyForm from "features/health-safety-forms/actions/types";
import * as fromHealthAndSafety from "features/health-and-safety/actions/types";

// DEBUG - Log the imported actions and types
// console.log("DEBUG - Imported fromHealthAndSafety:", fromHealthAndSafety);
console.log("DEBUG - Imported action creator:", onSiteInspectionChecklistCreateRequest);
// console.log("DEBUG - CREATE_REQUESTED type:", fromHealthAndSafety.SITE_INSPECTION_CHECKLIST_CREATE_REQUESTED);

// Hard-code the site inspection checklists route for navigation
const SITE_INSPECTION_CHECKLISTS_ROUTE = "/info/site-inspection-checklists";

// Create a fixed version of the action creator to ensure type is defined
const createSiteInspectionChecklist = (payload) => {
    console.log("DEBUG - Creating action with explicit type");
    // Use the actual imported action creator instead of manually creating an action
    return onSiteInspectionChecklistCreateRequest(payload);
};

// Constants for our action types
const CREATE_SUCCESS = "SITE_INSPECTION_CHECKLIST_CREATE_SUCCESS";
const CREATE_FAILED = "SITE_INSPECTION_CHECKLIST_CREATE_FAILED";

// Function to create CardHeader with consistent styling for section titles
const SectionHeader = ({ title, classes }) => (
    <>
        <Divider className={classes.divider} />
        <CardHeader
            className={classes.subsection}
            title={title}
            titleTypographyProps={{ variant: "h5", align: "left" }}
            disableTypography={false}
        />
    </>
);

const useStyles = makeStyles((theme) => ({
    root: {
        width: "100%",
    },
    actions: {
        marginTop: theme.spacing(2),
        paddingBottom: theme.spacing(8),
    },
    error: {
        color: theme.palette.error.main,
    },
    section: {
        marginBottom: theme.spacing(4),
    },
    sectionTitle: {
        marginBottom: theme.spacing(2),
        fontWeight: "bold",
    },
    subsection: {
        margin: theme.spacing(2, 0),
        paddingLeft: 0,
        '& .MuiCardHeader-title': {
            fontWeight: 'bold',
            fontSize: '1.1rem',
        },
        '& .MuiCardHeader-content': {
            textAlign: 'left',
        },
    },
    divider: {
        margin: theme.spacing(3, 0, 2, 0),
        height: 2,
    },
    fieldGroup: {
        marginBottom: theme.spacing(2),
    },
    remedialActions: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
        marginBottom: theme.spacing(2),
    },
    remedialAction: {
        marginBottom: theme.spacing(2),
    },
    addButton: {
        marginTop: theme.spacing(1),
    },
    card: {
        margin: theme.spacing(2, 0),
    },
    cardContent: {
        paddingTop: 0,
    },
    heading: {
        padding: theme.spacing(3, 2),
    },
    loadingWrapper: {
        position: 'relative',
    },
    buttonProgress: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12,
    },
    readOnlyField: {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.text.disabled,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.action.disabled,
        },
        '& .MuiInputBase-input': {
            cursor: 'default',
            '-webkit-text-fill-color': theme.palette.text.primary,
            opacity: 1,
        }
    }
}));

const AddSiteInspectionChecklistForm = ({ dispatch, saving, savedSuccess, error, staffList }) => {
    const classes = useStyles();
    const { history, match } = useRouter();
    const [formData, setFormData] = useState({
        id: uuid.v4(),
        siteId: "",
        location: "",
        inspector: "",
        date: new Date().toISOString(),
        
        // Site Control
        hazardBoardSignage: "",
        environmentalPlanIssues: "",
        toolboxTalk: "",
        toolboxTalkDate: "",
        safetyInductions: "",
        safetyNoticeBoard: "",
        
        // Site Facilities
        officesClean: "",
        smokoShedsClean: "",
        toiletsClean: "",
        toolEquipmentShedsAdequate: "",
        
        // General Site Tidiness & Access Ways
        clearSafeAccess: "",
        stairwaysAccessWaysClear: "",
        hoardingsFenceSecure: "",
        looseMaterialsSecure: "",
        
        // Personal Safety Equipment
        signageDisplayedLegible: "",
        hardhatsWorn: "",
        correctFootwear: "",
        glassesEarMuffsVestsMasks: "",
        
        // First Aid / Fire Prevention
        firstAidBox: "",
        accidentRegister: "",
        fireExtinguishersAvailable: "",
        fireExtinguishersCurrent: "",
        fireExtinguishersSufficientNumber: "",
        evacuationProcedureCurrent: "",
        allEmergenciesIncluded: "",
        
        // Lifting Operations (Updated from Cranes / Hoist / Lifting Equipment)
        liftAssessmentPlan: "",
        craneCertificationCurrent: "",
        slingsChainsLifting: "",
        operatorProcedures: "",
        inspectionsDone: "",
        manCageAvailable: "",
        emergencyPlanLifting: "",
        
        // Compressed Air (Updated from Compressed Air Equipment)
        compressedAirCondition: "",
        compressedAirGuards: "",
        compressedAirTrainedUser: "",
        
        // Excavations / Shoring (Updated from Excavations)
        excavationsShored: "",
        
        // Hot Work / Welding (Updated from Welding / Gas Cutting)
        hotWorkPermits: "",
        weldingFireExtinguishers: "",
        weldingOperatorsPPE: "",
        
        // Electrical Safety (Updated from Electrical Equipment)
        mainBoardLockable: "",
        currentTaggedLeads: "",
        currentTaggedPlant: "",
        currentTaggedLifeguards: "",
        leadsSafelyPlaced: "",
        equipmentGoodCondition: "",
        appropriateGuardsEquipment: "",
        adequateTemporaryLighting: "",
        
        // Chemical & Hazardous Substances (Updated from Chemicals)
        chemicalsCorrectlyStored: "",
        sdsAvailable: "",
        chemicalOperatorsPPE: "",
        
        // Tools & Task Analysis (Updated from Tools)
        patToolWof: "",
        staffTrainedTools: "",
        patSignage: "",
        
        // Scaffolding / Work Platforms (Updated from Scaffolding)
        scaffoldingNotifiableWeekly: "",
        scaffoldingHandrails: "",
        scaffoldingToeBoards: "",
        scaffoldingPlatforms: "",
        scaffoldingLadders: "",
        scaffoldingBaseSound: "",
        scaffoldingWorkPlatformsClear: "",
        scaffoldingPlatformsTripFree: "",
        scaffoldingPlanksTied: "",
        scaffoldingHeadroomClear: "",
        scaffoldingTiesBracing: "",
        
        // Ladder Use (Updated from Ladders)
        laddersGoodCondition: "",
        laddersSecured: "",
        laddersStays: "",
        laddersWorkingStepsDown: "",
        
        // Working at Height / Fall Hazards (Updated from Fall Hazards)
        fallHazardsFloorEdges: "",
        fallHazardsLiftShafts: "",
        
        // Remedial Actions
        remedialActions: [],
        save: { saving: false }
    });

    // Navigate to the list page on success
    useEffect(() => {
        if (savedSuccess) {
            console.log("DEBUG - Save successful, navigating to checklist list");
            history.push(SITE_INSPECTION_CHECKLISTS_ROUTE);
        }
    }, [savedSuccess, history]);

    // Get the list on component mount and reset form state
    useEffect(() => {
        let isMounted = true;
        
        if (isMounted) {
            console.log("DEBUG - Component mounted, resetting state to prevent redirect");
            // First reset the form state to prevent immediate redirect
            dispatch(onSiteInspectionChecklistFormReset());
            
            // Then request the checklist list and staff list
            setTimeout(() => {
                if (isMounted) {
                    console.log("DEBUG - Requesting site inspection checklist list");
                    dispatch(onSiteInspectionChecklistListRequest());
                    dispatch(onStaffListRequest());
                }
            }, 100);
        }
        
        return () => {
            isMounted = false;
        };
    }, [dispatch]);

    const onSubmit = (event) => {
        event.preventDefault();
        console.log("DEBUG - Form submitted:", formData);
        
        if (!formData.siteId) {
            console.error("DEBUG - No site ID provided");
            return;
        }
        
        // Always use the current date and time for the inspection
        const currentDate = new Date();
        const currentDateISOString = currentDate.toISOString();

        // Normalise toolboxTalkDate to a nullable ISO string
        let normalisedToolboxTalkDate = null;
        if (
            formData.toolboxTalk === "yes" &&
            formData.toolboxTalkDate &&
            String(formData.toolboxTalkDate).trim() !== ""
        ) {
            try {
                // TextField type="date" gives yyyy-MM-dd; convert to full ISO for the API
                const parsed = new Date(formData.toolboxTalkDate);
                if (!isNaN(parsed.getTime())) {
                    normalisedToolboxTalkDate = parsed.toISOString();
                }
            } catch (e) {
                // leave as null if parsing fails
            }
        }

        // Create submission data with current date and time and normalised toolboxTalkDate
        const submissionData = {
            ...formData,
            date: currentDateISOString, // Always use current date/time, regardless of form data
            createdAt: currentDateISOString,
            toolboxTalkDate: normalisedToolboxTalkDate,
        };

        console.log("DEBUG - Submission data with current date/time:", submissionData);
        
        // Dispatch action to create checklist
        dispatch(onSiteInspectionChecklistCreateRequest(submissionData));
    };

    const onCancel = () => {
        console.log("DEBUG - Form cancelled");
        history.push(SITE_INSPECTION_CHECKLISTS_ROUTE);
    };

    const handleTextChange = (field) => (event) => {
        // Handle both direct values from SearchDropdown and event objects from TextField
        const value = event && event.target ? event.target.value : event;
        console.log(`DEBUG - Text changed for ${field}:`, value);
        setFormData({
            ...formData,
            [field]: value,
        });
    };

    const handleCheckboxChange = (field) => (event) => {
        console.log(`DEBUG - Checkbox changed for ${field}:`, event.target.checked);
        setFormData({
            ...formData,
            [field]: event.target.checked,
        });
    };

    const handleRemedialActionChange = (index, field) => (eventOrValue) => {
        // Handle different input types
        let value;
        
        // Check if this is an event object (from TextField or Checkbox)
        if (eventOrValue && eventOrValue.target) {
            // It's an event object
            value = field === 'completed' ? eventOrValue.target.checked : eventOrValue.target.value;
        } else {
            // It's a direct value from SearchDropdown
            value = eventOrValue;
        }
        
        console.log(`DEBUG - Remedial action changed for index ${index}, field ${field}:`, value);
        
        const newRemedialActions = [...formData.remedialActions];
        newRemedialActions[index] = {
            ...newRemedialActions[index],
            [field]: value
        };
        
        setFormData({
            ...formData,
            remedialActions: newRemedialActions
        });
    };

    const addRemedialAction = () => {
        console.log("DEBUG - Adding remedial action");
        // Only add one remedial action if none exist
        if (formData.remedialActions.length === 0) {
            setFormData({
                ...formData,
                remedialActions: [
                    {
                        action: "",
                        comments: "",
                        person: "",
                        completed: false
                    }
                ]
            });
        }
    };

    const removeRemedialAction = (index) => {
        console.log(`DEBUG - Removing remedial action at index ${index}`);
        const newRemedialActions = [...formData.remedialActions];
        newRemedialActions.splice(index, 1);
        
        setFormData({
            ...formData,
            remedialActions: newRemedialActions
        });
    };

    const handleSiteChange = (formValues) => {
        console.log("DEBUG - Site form values changed:", formValues);
        if (formValues && formValues.siteId) {
            setFormData({
                ...formData,
                siteId: formValues.siteId
            });
        }
    };

    // Use the route defined in the health-and-safety types
    const INFO_ROUTE = fromHealthSafetyForm.INFO_ROUTE;

    // Add handleRadioChange function
    const handleRadioChange = (field) => (value) => {
        console.log(`DEBUG - Radio changed for ${field}:`, value);
        setFormData({
            ...formData,
            [field]: value,
        });
    };

    // Options for Yes/No/NA radio buttons
    const options = [
        {
            label: "Yes",
            value: "yes",
        },
        {
            label: "No",
            value: "no",
        },
        {
            label: "NA",
            value: "na",
        },
    ];

    return (
        <Page title="+ Site Inspection Checklist">
            <ScrollToTopOnMount />
            <PageHeaderTabs 
                title="+ Site Inspection Checklist"
                addTabPath={match.url}
                hideAddTabButton={true}
                listTabPath={fromHealthAndSafety.INFO_SITE_INSPECTION_CHECKLISTS_ROUTE}
                backActionPath={INFO_ROUTE}
            />
            <PageContent padSides padBottom padTop>
                {error && (
                    <Typography variant="body1" className={classes.error} gutterBottom>
                        Error: {error}
                    </Typography>
                )}
                
                <ValidatorForm 
                    autoComplete="off"
                    noValidate
                    onSubmit={onSubmit}
                    className={classes.root}
                >
                    <Grid container spacing={3}>
                        <Grid item lg={6} md={12} sm={12} xs={12}>
                            <SiteForm 
                                values={{
                                    siteId: formData.siteId || '',
                                    save: { saving: false },
                                    isLoading: false,
                                    createdAt: new Date().toISOString().substring(0, 10)
                                }}
                                onChange={handleSiteChange}
                                hideDate={true}
                            />
                            
                            {formData.siteId && (
                                <Card className={classes.card}>
                                    <CardHeader
                                        className={classes.heading}
                                        title="Site Inspection Checklist"
                                        titleTypographyProps={{ variant: "h4" }}
                                    />
                                    <CardContent className={classes.cardContent}>
                                        {/* Site Control Section */}
                                        <SectionHeader title="1. Site Control" classes={classes} />
                                        
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Hazard Board Signage
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="hazardBoardSignage"
                                                    initialValue={formData.hazardBoardSignage}
                                                    options={options}
                                                    onChange={handleRadioChange('hazardBoardSignage')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Environmental Plan Issues
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="environmentalPlanIssues"
                                                    initialValue={formData.environmentalPlanIssues}
                                                    options={options}
                                                    onChange={handleRadioChange('environmentalPlanIssues')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Toolbox Talk
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="toolboxTalk"
                                                    initialValue={formData.toolboxTalk}
                                                    options={options}
                                                    onChange={handleRadioChange('toolboxTalk')}
                                                />
                                            </Grid>
                                            
                                            {formData.toolboxTalk === "yes" && (
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Toolbox Talk Date"
                                                        type="date"
                                                        variant="outlined"
                                                        value={formData.toolboxTalkDate}
                                                        onChange={handleTextChange('toolboxTalkDate')}
                                                        InputLabelProps={{
                                                            shrink: true,
                                                        }}
                                                    />
                                                </Grid>
                                            )}
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Safety Inductions
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="safetyInductions"
                                                    initialValue={formData.safetyInductions}
                                                    options={options}
                                                    onChange={handleRadioChange('safetyInductions')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Safety Notice Board
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="safetyNoticeBoard"
                                                    initialValue={formData.safetyNoticeBoard}
                                                    options={options}
                                                    onChange={handleRadioChange('safetyNoticeBoard')}
                                                />
                                            </Grid>
                                        </Grid>
                                    
                                        {/* Site Facilities Section */}
                                        <SectionHeader title="2. Site Facilities" classes={classes} />
                                        
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Offices Clean
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="officesClean"
                                                    initialValue={formData.officesClean}
                                                    options={options}
                                                    onChange={handleRadioChange('officesClean')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Smoko Sheds Clean
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="smokoShedsClean"
                                                    initialValue={formData.smokoShedsClean}
                                                    options={options}
                                                    onChange={handleRadioChange('smokoShedsClean')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Toilets Clean
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="toiletsClean"
                                                    initialValue={formData.toiletsClean}
                                                    options={options}
                                                    onChange={handleRadioChange('toiletsClean')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Tool & Equipment Sheds Adequate
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="toolEquipmentShedsAdequate"
                                                    initialValue={formData.toolEquipmentShedsAdequate}
                                                    options={options}
                                                    onChange={handleRadioChange('toolEquipmentShedsAdequate')}
                                                />
                                            </Grid>
                                        </Grid>
                                    
                                        {/* General Site Tidiness & Access Ways section */}
                                        <SectionHeader title="3. General Site Tidiness & Access Ways" classes={classes} />
                                    
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Clear Safe Access
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="clearSafeAccess"
                                                    initialValue={formData.clearSafeAccess}
                                                    options={options}
                                                    onChange={handleRadioChange('clearSafeAccess')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Stairways & Access Ways Clear
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="stairwaysAccessWaysClear"
                                                    initialValue={formData.stairwaysAccessWaysClear}
                                                    options={options}
                                                    onChange={handleRadioChange('stairwaysAccessWaysClear')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Hoardings & Fence Secure
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="hoardingsFenceSecure"
                                                    initialValue={formData.hoardingsFenceSecure}
                                                    options={options}
                                                    onChange={handleRadioChange('hoardingsFenceSecure')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Loose Materials Secure
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="looseMaterialsSecure"
                                                    initialValue={formData.looseMaterialsSecure}
                                                    options={options}
                                                    onChange={handleRadioChange('looseMaterialsSecure')}
                                                />
                                            </Grid>
                                        </Grid>
                                    
                                        {/* Personal Safety Equipment section */}
                                        <SectionHeader title="4. Personal Safety Equipment" classes={classes} />
                                    
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Signage Displayed & Legible
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="signageDisplayedLegible"
                                                    initialValue={formData.signageDisplayedLegible}
                                                    options={options}
                                                    onChange={handleRadioChange('signageDisplayedLegible')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Hard Hats Worn
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="hardhatsWorn"
                                                    initialValue={formData.hardhatsWorn}
                                                    options={options}
                                                    onChange={handleRadioChange('hardhatsWorn')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Correct Footwear
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="correctFootwear"
                                                    initialValue={formData.correctFootwear}
                                                    options={options}
                                                    onChange={handleRadioChange('correctFootwear')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Glasses / Ear Muffs / Vests / Masks
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="glassesEarMuffsVestsMasks"
                                                    initialValue={formData.glassesEarMuffsVestsMasks}
                                                    options={options}
                                                    onChange={handleRadioChange('glassesEarMuffsVestsMasks')}
                                                />
                                            </Grid>
                                        </Grid>
                                    
                                        {/* First Aid / Fire Prevention section */}
                                        <SectionHeader title="5. First Aid / Fire Prevention" classes={classes} />
                                        
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    First Aid Box
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="firstAidBox"
                                                    initialValue={formData.firstAidBox}
                                                    options={options}
                                                    onChange={handleRadioChange('firstAidBox')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Accident Register
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="accidentRegister"
                                                    initialValue={formData.accidentRegister}
                                                    options={options}
                                                    onChange={handleRadioChange('accidentRegister')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Fire Extinguishers Available
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="fireExtinguishersAvailable"
                                                    initialValue={formData.fireExtinguishersAvailable}
                                                    options={options}
                                                    onChange={handleRadioChange('fireExtinguishersAvailable')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Fire Extinguishers Current
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="fireExtinguishersCurrent"
                                                    initialValue={formData.fireExtinguishersCurrent}
                                                    options={options}
                                                    onChange={handleRadioChange('fireExtinguishersCurrent')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Fire Extinguishers Sufficient Number
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="fireExtinguishersSufficientNumber"
                                                    initialValue={formData.fireExtinguishersSufficientNumber}
                                                    options={options}
                                                    onChange={handleRadioChange('fireExtinguishersSufficientNumber')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Evacuation Procedure Current
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="evacuationProcedureCurrent"
                                                    initialValue={formData.evacuationProcedureCurrent}
                                                    options={options}
                                                    onChange={handleRadioChange('evacuationProcedureCurrent')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    All Emergencies Included
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="allEmergenciesIncluded"
                                                    initialValue={formData.allEmergenciesIncluded}
                                                    options={options}
                                                    onChange={handleRadioChange('allEmergenciesIncluded')}
                                                />
                                            </Grid>
                                        </Grid>
                                        
                                        {/* Lifting Operations (Updated from Cranes / Hoist / Lifting Equipment) section */}
                                        <SectionHeader title="6. Lifting Operations" classes={classes} />
                                        
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Lift Assessment and Plan
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="liftAssessmentPlan"
                                                    initialValue={formData.liftAssessmentPlan}
                                                    options={options}
                                                    onChange={handleRadioChange('liftAssessmentPlan')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Crane Certification Current
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="craneCertificationCurrent"
                                                    initialValue={formData.craneCertificationCurrent}
                                                    options={options}
                                                    onChange={handleRadioChange('craneCertificationCurrent')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Slings/Chains/Lifting Equipment Certified
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="slingsChainsLifting"
                                                    initialValue={formData.slingsChainsLifting}
                                                    options={options}
                                                    onChange={handleRadioChange('slingsChainsLifting')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Operator Procedures in Place
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="operatorProcedures"
                                                    initialValue={formData.operatorProcedures}
                                                    options={options}
                                                    onChange={handleRadioChange('operatorProcedures')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Inspections Done
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="inspectionsDone"
                                                    initialValue={formData.inspectionsDone}
                                                    options={options}
                                                    onChange={handleRadioChange('inspectionsDone')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Man Cage Available
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="manCageAvailable"
                                                    initialValue={formData.manCageAvailable}
                                                    options={options}
                                                    onChange={handleRadioChange('manCageAvailable')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Emergency Plan for Lifting
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="emergencyPlanLifting"
                                                    initialValue={formData.emergencyPlanLifting}
                                                    options={options}
                                                    onChange={handleRadioChange('emergencyPlanLifting')}
                                                />
                                            </Grid>
                                        </Grid>
                                        
                                        {/* Compressed Air section */}
                                        <SectionHeader title="7. Compressed Air" classes={classes} />
                                        
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Compressed Air in Good Condition
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="compressedAirCondition"
                                                    initialValue={formData.compressedAirCondition}
                                                    options={options}
                                                    onChange={handleRadioChange('compressedAirCondition')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Guards in Place
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="compressedAirGuards"
                                                    initialValue={formData.compressedAirGuards}
                                                    options={options}
                                                    onChange={handleRadioChange('compressedAirGuards')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Trained Users Only
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="compressedAirTrainedUser"
                                                    initialValue={formData.compressedAirTrainedUser}
                                                    options={options}
                                                    onChange={handleRadioChange('compressedAirTrainedUser')}
                                                />
                                            </Grid>
                                        </Grid>
                                        
                                        {/* Excavations / Shoring section */}
                                        <SectionHeader title="8. Excavations / Shoring" classes={classes} />
                                        
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Excavations Properly Shored
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="excavationsShored"
                                                    initialValue={formData.excavationsShored}
                                                    options={options}
                                                    onChange={handleRadioChange('excavationsShored')}
                                                />
                                            </Grid>
                                        </Grid>
                                        
                                        {/* Hot Work / Welding section */}
                                        <SectionHeader title="9. Hot Work / Welding" classes={classes} />
                                        
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Hot Work Permits
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="hotWorkPermits"
                                                    initialValue={formData.hotWorkPermits}
                                                    options={options}
                                                    onChange={handleRadioChange('hotWorkPermits')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Fire Extinguishers at Hand
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="weldingFireExtinguishers"
                                                    initialValue={formData.weldingFireExtinguishers}
                                                    options={options}
                                                    onChange={handleRadioChange('weldingFireExtinguishers')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Operators Using PPE
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="weldingOperatorsPPE"
                                                    initialValue={formData.weldingOperatorsPPE}
                                                    options={options}
                                                    onChange={handleRadioChange('weldingOperatorsPPE')}
                                                />
                                            </Grid>
                                        </Grid>
                                        
                                        {/* Electrical Safety section */}
                                        <SectionHeader title="10. Electrical Safety" classes={classes} />
                                        
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Main Board Lockable & Safe
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="mainBoardLockable"
                                                    initialValue={formData.mainBoardLockable}
                                                    options={options}
                                                    onChange={handleRadioChange('mainBoardLockable')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Current Tagged Leads
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="currentTaggedLeads"
                                                    initialValue={formData.currentTaggedLeads}
                                                    options={options}
                                                    onChange={handleRadioChange('currentTaggedLeads')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Current Tagged Plant
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="currentTaggedPlant"
                                                    initialValue={formData.currentTaggedPlant}
                                                    options={options}
                                                    onChange={handleRadioChange('currentTaggedPlant')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Current Tagged Lifeguards
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="currentTaggedLifeguards"
                                                    initialValue={formData.currentTaggedLifeguards}
                                                    options={options}
                                                    onChange={handleRadioChange('currentTaggedLifeguards')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Leads Safely Placed
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="leadsSafelyPlaced"
                                                    initialValue={formData.leadsSafelyPlaced}
                                                    options={options}
                                                    onChange={handleRadioChange('leadsSafelyPlaced')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Equipment in Good Condition
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="equipmentGoodCondition"
                                                    initialValue={formData.equipmentGoodCondition}
                                                    options={options}
                                                    onChange={handleRadioChange('equipmentGoodCondition')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Appropriate Guards on Equipment
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="appropriateGuardsEquipment"
                                                    initialValue={formData.appropriateGuardsEquipment}
                                                    options={options}
                                                    onChange={handleRadioChange('appropriateGuardsEquipment')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Adequate Temporary Lighting
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="adequateTemporaryLighting"
                                                    initialValue={formData.adequateTemporaryLighting}
                                                    options={options}
                                                    onChange={handleRadioChange('adequateTemporaryLighting')}
                                                />
                                            </Grid>
                                        </Grid>
                                        
                                        {/* Chemical & Hazardous Substances section */}
                                        <SectionHeader title="11. Chemical & Hazardous Substances" classes={classes} />
                                        
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Chemicals Correctly Stored
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="chemicalsCorrectlyStored"
                                                    initialValue={formData.chemicalsCorrectlyStored}
                                                    options={options}
                                                    onChange={handleRadioChange('chemicalsCorrectlyStored')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    SDS Available
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="sdsAvailable"
                                                    initialValue={formData.sdsAvailable}
                                                    options={options}
                                                    onChange={handleRadioChange('sdsAvailable')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Operators Using PPE
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="chemicalOperatorsPPE"
                                                    initialValue={formData.chemicalOperatorsPPE}
                                                    options={options}
                                                    onChange={handleRadioChange('chemicalOperatorsPPE')}
                                                />
                                            </Grid>
                                        </Grid>
                                        
                                        {/* Tools & Task Analysis section */}
                                        <SectionHeader title="12. Tools & Task Analysis" classes={classes} />
                                        
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    PAT / TOOL WOF (12mth)
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="patToolWof"
                                                    initialValue={formData.patToolWof}
                                                    options={options}
                                                    onChange={handleRadioChange('patToolWof')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Staff Trained to Use Tools
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="staffTrainedTools"
                                                    initialValue={formData.staffTrainedTools}
                                                    options={options}
                                                    onChange={handleRadioChange('staffTrainedTools')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    PAT Signage & Job Safety Sheets
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="patSignage"
                                                    initialValue={formData.patSignage}
                                                    options={options}
                                                    onChange={handleRadioChange('patSignage')}
                                                />
                                            </Grid>
                                        </Grid>
                                        
                                        {/* Scaffolding / Work Platforms section */}
                                        <SectionHeader title="13. Scaffolding / Work Platforms" classes={classes} />
                                        
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Notifiable / Weekly Inspections Done
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="scaffoldingNotifiableWeekly"
                                                    initialValue={formData.scaffoldingNotifiableWeekly}
                                                    options={options}
                                                    onChange={handleRadioChange('scaffoldingNotifiableWeekly')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Handrails
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="scaffoldingHandrails"
                                                    initialValue={formData.scaffoldingHandrails}
                                                    options={options}
                                                    onChange={handleRadioChange('scaffoldingHandrails')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Toe Boards
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="scaffoldingToeBoards"
                                                    initialValue={formData.scaffoldingToeBoards}
                                                    options={options}
                                                    onChange={handleRadioChange('scaffoldingToeBoards')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Platforms Sound
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="scaffoldingPlatforms"
                                                    initialValue={formData.scaffoldingPlatforms}
                                                    options={options}
                                                    onChange={handleRadioChange('scaffoldingPlatforms')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Ladders Tied & 1m Overhang
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="scaffoldingLadders"
                                                    initialValue={formData.scaffoldingLadders}
                                                    options={options}
                                                    onChange={handleRadioChange('scaffoldingLadders')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Base Sound and Level
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="scaffoldingBaseSound"
                                                    initialValue={formData.scaffoldingBaseSound}
                                                    options={options}
                                                    onChange={handleRadioChange('scaffoldingBaseSound')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Work Platforms Clear & Safe
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="scaffoldingWorkPlatformsClear"
                                                    initialValue={formData.scaffoldingWorkPlatformsClear}
                                                    options={options}
                                                    onChange={handleRadioChange('scaffoldingWorkPlatformsClear')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Platforms Trip Free
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="scaffoldingPlatformsTripFree"
                                                    initialValue={formData.scaffoldingPlatformsTripFree}
                                                    options={options}
                                                    onChange={handleRadioChange('scaffoldingPlatformsTripFree')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Planks Tied
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="scaffoldingPlanksTied"
                                                    initialValue={formData.scaffoldingPlanksTied}
                                                    options={options}
                                                    onChange={handleRadioChange('scaffoldingPlanksTied')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Headroom Clear
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="scaffoldingHeadroomClear"
                                                    initialValue={formData.scaffoldingHeadroomClear}
                                                    options={options}
                                                    onChange={handleRadioChange('scaffoldingHeadroomClear')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Ties / Bracing
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="scaffoldingTiesBracing"
                                                    initialValue={formData.scaffoldingTiesBracing}
                                                    options={options}
                                                    onChange={handleRadioChange('scaffoldingTiesBracing')}
                                                />
                                            </Grid>
                                        </Grid>
                                        
                                        {/* Ladder Use section */}
                                        <SectionHeader title="14. Ladder Use" classes={classes} />
                                        
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Ladders in Good Condition
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="laddersGoodCondition"
                                                    initialValue={formData.laddersGoodCondition}
                                                    options={options}
                                                    onChange={handleRadioChange('laddersGoodCondition')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Ladders Secured
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="laddersSecured"
                                                    initialValue={formData.laddersSecured}
                                                    options={options}
                                                    onChange={handleRadioChange('laddersSecured')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Stays
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="laddersStays"
                                                    initialValue={formData.laddersStays}
                                                    options={options}
                                                    onChange={handleRadioChange('laddersStays')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Working 3 Steps Down on Ladder
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="laddersWorkingStepsDown"
                                                    initialValue={formData.laddersWorkingStepsDown}
                                                    options={options}
                                                    onChange={handleRadioChange('laddersWorkingStepsDown')}
                                                />
                                            </Grid>
                                        </Grid>
                                        
                                        {/* Working at Height / Fall Hazards section */}
                                        <SectionHeader title="15. Working at Height / Fall Hazards" classes={classes} />
                                        
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Floor Edges, Holes, Open Areas Protected
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="fallHazardsFloorEdges"
                                                    initialValue={formData.fallHazardsFloorEdges}
                                                    options={options}
                                                    onChange={handleRadioChange('fallHazardsFloorEdges')}
                                                />
                                            </Grid>
                                            
                                            <Grid item xs={12}>
                                                <Typography align="left" gutterBottom variant="body1">
                                                    Lift Shafts, Pits Protected / Access Controlled
                                                </Typography>
                                                <RadioListInput
                                                    data-cy="fallHazardsLiftShafts"
                                                    initialValue={formData.fallHazardsLiftShafts}
                                                    options={options}
                                                    onChange={handleRadioChange('fallHazardsLiftShafts')}
                                                />
                                            </Grid>
                                        </Grid>
                                        
                                        {/* Remedial Action Required section */}
                                        <SectionHeader title="Remedial Action Required" classes={classes} />
                                    
                                        {formData.remedialActions.map((action, index) => (
                                            <Paper key={index} className={classes.remedialActions}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12}>
                                                        <TextField
                                                            fullWidth
                                                            label="Action"
                                                            variant="outlined"
                                                            value={action.action}
                                                            onChange={handleRemedialActionChange(index, 'action')}
                                                        />
                                                    </Grid>
                                                    
                                                    <Grid item xs={12}>
                                                        <TextField
                                                            fullWidth
                                                            label="Comments"
                                                            variant="outlined"
                                                            value={action.comments}
                                                            onChange={handleRemedialActionChange(index, 'comments')}
                                                            multiline
                                                            rows={2}
                                                        />
                                                    </Grid>
                                                    
                                                    <Grid item xs={12} md={8}>
                                                        <SearchDropdown
                                                            id={`person-responsible-${index}`}
                                                            label="Person Responsible"
                                                            value={action.person}
                                                            onChange={handleRemedialActionChange(index, 'person')}
                                                            defaultOptions={staffList && staffList.map(staff => ({
                                                                label: `${staff.firstName} ${staff.lastName || ''}`.trim(),
                                                                value: `${staff.firstName} ${staff.lastName || ''}`.trim()
                                                            }))}
                                                        />
                                                    </Grid>
                                                    
                                                    <Grid item xs={12}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={action.completed}
                                                                    onChange={handleRemedialActionChange(index, 'completed')}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label="Completed"
                                                        />
                                                    </Grid>
                                                    
                                                    <Grid item xs={12}>
                                                        <Button
                                                            variant="outlined"
                                                            color="secondary"
                                                            onClick={() => removeRemedialAction(index)}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </Paper>
                                        ))}
                                        
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={addRemedialAction}
                                            className={classes.addButton}
                                        >
                                            Add Remedial Action
                                        </Button>
                                    </CardContent>
                                </Card>
                            )}
                        </Grid>
                    </Grid>

                    <FormActions className={classes.actions}>
                        <div className={classes.loadingWrapper}>
                            <FormSubmitButton disabled={!formData.siteId || saving}>
                                {saving ? "Saving..." : "Save"}
                            </FormSubmitButton>
                            {saving && <CircularProgress size={24} className={classes.buttonProgress} />}
                        </div>
                        <FormButton onClick={onCancel} disabled={saving}>
                            Cancel
                        </FormButton>
                    </FormActions>
                </ValidatorForm>
            </PageContent>
        </Page>
    );
};

AddSiteInspectionChecklistForm.propTypes = {
    dispatch: PropTypes.func.isRequired,
    saving: PropTypes.bool,
    savedSuccess: PropTypes.bool,
    error: PropTypes.string,
    staffList: PropTypes.array
};

const mapStateToProps = (state) => ({
    saving: state.healthSafety && state.healthSafety.saving,
    savedSuccess: state.healthSafety && state.healthSafety.savedSuccess,
    error: state.healthSafety && state.healthSafety.error,
    staffList: state.staff && state.staff.pagination && state.staff.pagination.results
});

export default connect(mapStateToProps)(AddSiteInspectionChecklistForm); 